import { JcCommonProps, JcUtils } from '../../utils/JcUtils';
import { shouldInterceptLinkClick } from '../../utils/JcLinkUtils';
import { JcButton, JcButtonType } from './JcButton';
import { JcLink } from '../JcLink';

import React from 'react';
import { JcRow } from '../JcBox';

/** `id` is the option's value, so the DOM id from JcCommonProps is omitted. */
export interface JcSelectionOption<T extends string> extends Omit<JcCommonProps, "id"> {
  id: T;
  label: React.ReactNode;
  /** Accessible name when the visible label isn't descriptive (e.g. an abbreviation). */
  ariaLabel?: string;
  onClick?: () => void;
  disabled?: boolean;
  /**
   * When set, the option renders as an anchor (`<a href>`) instead of a button —
   * use for selections that are really URL-backed navigation (e.g. tabs whose
   * state lives in the route). `onClick` still fires for plain left-clicks (SPA
   * navigation); modifier-clicks fall through so "open in new tab" works.
   */
  href?: string;
}

export interface JcSelectionBarProps<T extends string = string> extends JcCommonProps {
  readonly selectedID?: T;
  readonly options: JcSelectionOption<T>[];
  readonly onClick?: (id: T) => void;
  /**
   * Compact rounded chips, the selection filled — for a small in-place view
   * toggle inside a panel. The default (underlined tab) look is for a row of
   * peer sections, e.g. tabs.
   */
  readonly pill?: boolean;
}


export const JcSelectionBar = <T extends string>(props: JcSelectionBarProps<T>) => {

  const commonProps = JcUtils.commonProps(props, ["jc-button-bar", props.pill && "jc-pill-bar"]);

  return <JcRow {...commonProps}>
    {props.options.map(option => {
      const onClick = option.onClick || (props.onClick && (() => props.onClick && props.onClick(option.id)));
      const selected = option.id === props.selectedID;

      // URL-backed option → real anchor, so middle-/Cmd-click open in a new tab.
      // Plain left-clicks are intercepted and routed through onClick (SPA nav).
      const optionCommonProps = JcUtils.commonProps(
        { className: option.className, style: option.style, "data-testid": option["data-testid"] },
        ["jc-button-bar-button"],
      );

      if (option.href !== undefined) {
        const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
          if (shouldInterceptLinkClick(event)) {
            event.preventDefault();
            onClick?.();
          }
        };
        return <JcLink key={option.id + "-key"}
          {...optionCommonProps}
          buttonType={JcButtonType.GHOST}
          disabled={option.disabled}
          selected={selected}
          href={option.href}
          onClick={handleClick}
          ariaLabel={option.ariaLabel}
          label={option.label} />;
      }

      return <JcButton key={option.id + "-key"}
        {...optionCommonProps}
        buttonType={JcButtonType.GHOST}
        disabled={option.disabled}
        selected={selected}
        ariaLabel={option.ariaLabel}
        onClick={onClick}>{option.label}
      </JcButton>
    })}
  </JcRow>
}
